import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

const PROFILE_ADMIN_QUERY_KEY = 'profile-admin';

async function fetchProfileAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('admin')
    .eq('id', userId)
    .single();
  return (data && !error) ? (data.admin || false) : false;
}

/**
 * Hook que verifica se o usuário é admin da plataforma (flag admin no perfil).
 * Usa React Query para uma única requisição compartilhada entre todos os consumidores,
 * evitando N+1 e requisições duplicadas em /chat/inbox e demais páginas.
 */
export function useAdmin() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const email = user?.email ?? null;

  // Super-admin: não precisa consultar o banco
  const SUPER_ADMIN_EMAILS = ['fluityai@gmail.com', 'mnepomucena10@gmail.com'];
  const isSuperAdmin = !!email && SUPER_ADMIN_EMAILS.includes(email);

  const { data: adminFromProfile, isLoading, isFetched } = useQuery({
    queryKey: [PROFILE_ADMIN_QUERY_KEY, userId],
    queryFn: () => fetchProfileAdmin(userId!),
    enabled: !!userId && !isSuperAdmin,
    staleTime: 5 * 60 * 1000, // 5 min — evita refetch desnecessário
    gcTime: 30 * 60 * 1000,   // 30 min (antes cacheTime)
  });

  const isAdmin = isSuperAdmin || (adminFromProfile ?? false);
  const loading = !!userId && !isSuperAdmin && isLoading;

  return { isAdmin, loading };
}
